import React, { useState } from 'react';
import { Collapse, Tooltip } from '@mui/material';
import CallMadeRoundedIcon from '@mui/icons-material/CallMadeRounded';
import CallMissedRoundedIcon from '@mui/icons-material/CallMissedRounded';
import CallReceivedRoundedIcon from '@mui/icons-material/CallReceivedRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { useAppSelector } from 'hooks/store';
import { rerunCallScribeIntelligence } from 'services/callScribe';
import { extractApiErrorMessage } from 'services/callScribe/apiError';
import {
  CallScribeCallDirection,
  CallScribePipelineStage,
  ICallScribeCallListItem,
} from 'services/callScribe/types';
import { CallScribePipelineForCallSelector } from 'store/callScribe/selectors';

import CallChipRow, { CallStatusChip, sentimentChipFor } from 'components/CallScribe/callChips';
import Avatar from 'components/CallScribe/ui/Avatar';
import { SPEAKER_TONE_COUNT } from 'components/CallScribe/callScribeTheme';
import { recordingStoppedLabel } from 'components/CallScribe/recordingStop';
import useRecentsStyles from 'components/CallScribe/Recents/recentsStyles';
import {
  IGroupedCall,
  exactLocalLabel,
  formatDurationShort,
  formatPhoneE164ToDisplay,
  isUnansweredCall,
  otherPartyPhone,
  relativeTimeLabel,
  resolvePipelineRowState,
} from 'components/CallScribe/Recents/recentsHelpers';

export interface IRecentCallCardProps {
  group: IGroupedCall;
  onOpenCall: (callId: number) => void;
  onRedial: (phone: string, contactExternalId?: string) => void;
  redialDisabled?: boolean;
}

export const avatarToneFor = (name: string | null | undefined): number | null => {
  if (!name || !/\p{L}/u.test(name)) return null;
  const sum = [...name.trim().toLowerCase()].reduce((acc, ch) => acc + (ch.codePointAt(0) ?? 0), 0);
  return sum % SPEAKER_TONE_COUNT;
};

const PipelineInline: React.FC<{ call: ICallScribeCallListItem }> = ({ call }) => {
  const classes = useRecentsStyles();
  const live = useAppSelector(CallScribePipelineForCallSelector(call.id));
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const stage = live?.stage ?? call.pipelineStage;
  const error = live?.error ?? call.pipelineError;

  const rowState = resolvePipelineRowState(stage);

  if (rowState === 'failed') {
    const onRetry = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.stopPropagation();
      if (retrying) return;
      setRetrying(true);
      setRetryError(null);
      try {
        await rerunCallScribeIntelligence(call.id);
      } catch (err) {
        setRetryError(extractApiErrorMessage(err, 'Could not queue a re-run. Try again.'));
        setRetrying(false);
      }
    };
    return (
      <div className={classes.pipelineRow}>
        <Tooltip title={retryError ?? error ?? ''}>
          <span className={classes.pipelineError}>
            <ErrorOutlineIcon fontSize="inherit" />
            {retryError ?? 'Processing failed'}
          </span>
        </Tooltip>
        <button
          type="button"
          className={classes.pipelineRetry}
          onClick={e => { void onRetry(e); }}
          disabled={retrying}
        >
          <RefreshRoundedIcon aria-hidden />
          {retrying ? 'Retrying…' : 'Retry'}
        </button>
      </div>
    );
  }

  if (rowState === 'summaryReady') {
    if (call.hasIntelligence) return null;
    return (
      <div className={classes.pipelineRow}>
        <span className={classes.pipelineDone}>
          <CheckCircleOutlineIcon fontSize="inherit" />
          Summary ready
        </span>
      </div>
    );
  }

  if (rowState === 'inFlight') {
    const label = stage === CallScribePipelineStage.Summarizing
      ? 'Generating summary…'
      : 'Transcribing…';
    return (
      <div className={classes.pipelineRow}>
        <span className={classes.pipelineActive}>{label}</span>
      </div>
    );
  }

  if (call.hasIntelligence || call.summaryShort) return null;

  return (
    <div className={classes.pipelineRow}>
      <span className={classes.pipelineMuted}>
        {rowState === 'nothingToSummarise'
          ? 'Nothing to summarise'
          : 'No summary for this call'}
      </span>
    </div>
  );
};

const MemberRow: React.FC<{
  call: ICallScribeCallListItem;
  onOpenCall: (callId: number) => void;
}> = ({ call, onOpenCall }) => {
  const classes = useRecentsStyles();
  const onClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onOpenCall(call.id);
  };
  return (
    <button
      type="button"
      className={classes.memberRow}
      onClick={onClick}
      aria-label={`Open call details for ${
        relativeTimeLabel(call.startedAtUtc ?? call.createdAtUtc) || 'this call'
      }`}
    >
      <Tooltip title={exactLocalLabel(call.startedAtUtc ?? call.createdAtUtc)}>
        <span className={classes.memberTime}>
          {relativeTimeLabel(call.startedAtUtc ?? call.createdAtUtc)}
        </span>
      </Tooltip>
      {call.durationSeconds != null && call.durationSeconds >= 0 ? (
        <span className={classes.memberDuration}>
          {formatDurationShort(call.durationSeconds)}
        </span>
      ) : null}
      <CallStatusChip status={call.status} />
      {call.hasIntelligence ? sentimentChipFor(call.sentiment) : null}
      <span className={classes.memberSpacer} />
      <ChevronRightRoundedIcon className={classes.memberChevron} style={{ fontSize: 18 }} aria-hidden />
      {recordingStoppedLabel(call) ? (
        <span className={classes.memberStoppedLine}>{recordingStoppedLabel(call)}</span>
      ) : null}
    </button>
  );
};

const RecentCallCard: React.FC<IRecentCallCardProps> = ({
  group,
  onOpenCall,
  onRedial,
  redialDisabled,
}) => {
  const classes = useRecentsStyles();
  const [expanded, setExpanded] = useState(false);
  const call = group.representative;
  const isGroup = group.count > 1;

  const phone = otherPartyPhone(call);
  const formattedPhone = formatPhoneE164ToDisplay(phone) || '—';
  const displayName = call.contactName ?? formattedPhone;
  const subtitle = call.contactName ? formattedPhone : '';
  const unanswered = isUnansweredCall(call);

  const DirectionIcon = (() => {
    if (unanswered) return CallMissedRoundedIcon;
    if (call.direction === CallScribeCallDirection.Outbound) return CallMadeRoundedIcon;
    return CallReceivedRoundedIcon;
  })();

  const activate = (): void => {
    if (isGroup) {
      setExpanded(v => !v);
      return;
    }
    onOpenCall(call.id);
  };
  const onCardKey = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  };
  const stopRowActivation = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
  };
  const onRedialClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    if ((redialDisabled ?? false) || !phone) return;
    onRedial(phone, call.contactExternalId ?? undefined);
  };

  const startIso = call.startedAtUtc ?? call.createdAtUtc;
  const timeLabel = relativeTimeLabel(startIso);
  const durationLabel = call.durationSeconds != null && call.durationSeconds >= 0
    ? formatDurationShort(call.durationSeconds)
    : '';
  const redialBlocked = (redialDisabled ?? false) || !phone;

  return (
    <div
      className={classes.card}
      role="button"
      tabIndex={0}
      onClick={activate}
      onKeyDown={onCardKey}
      aria-expanded={isGroup ? expanded : undefined}
      aria-label={isGroup
        ? `${displayName}, ${group.count} calls — expand`
        : `Open call details for ${displayName}`}
    >
      <div className={classes.cardMain}>
        <Avatar
          name={call.contactName ?? ''}
          tone={avatarToneFor(call.contactName)}
          icon={unanswered ? <CallMissedRoundedIcon /> : <PhoneRoundedIcon />}
        />

        <div className={classes.body}>
          <div className={classes.primaryRow}>
            <Tooltip title={displayName}>
              <span className={`${classes.primaryLabel} ${unanswered ? classes.primaryLabelMissed : ''}`}>
                {displayName}
              </span>
            </Tooltip>
            {isGroup ? (
              <span
                className={`${classes.countBadge} ${unanswered ? classes.countBadgeMissed : ''}`}
                aria-label={`${group.count} calls`}
              >
                ({group.count})
              </span>
            ) : null}
            <Tooltip title={exactLocalLabel(startIso)}>
              <span className={classes.trailingTime}>{timeLabel}</span>
            </Tooltip>
          </div>

          <div className={classes.secondaryRow}>
            <span
              className={`${classes.directionIcon} ${unanswered ? classes.directionIconMissed : ''}`}
              aria-hidden="true"
            >
              <DirectionIcon style={{ fontSize: 15 }} />
            </span>
            <span className={classes.secondaryMeta}>
              {[subtitle, durationLabel].filter(Boolean).join(' · ')}
            </span>
          </div>

          {call.summaryShort ? (
            <Tooltip title={call.summaryShort}>
              <div className={classes.summaryLine}>{call.summaryShort}</div>
            </Tooltip>
          ) : null}

          {recordingStoppedLabel(call) ? (
            <div className={classes.summaryLine}>{recordingStoppedLabel(call)}</div>
          ) : null}

          <CallChipRow call={call} className={classes.chipsRow} />

          <PipelineInline call={call} />
        </div>

        <div className={classes.trailing}>
          <Tooltip title={redialBlocked ? '' : `Call ${displayName}`}>
            <span>
              <button
                type="button"
                className={classes.recallAction}
                onClick={onRedialClick}
                onKeyDown={stopRowActivation}
                disabled={redialBlocked}
                aria-label={`Call ${displayName}`}
              >
                <PhoneRoundedIcon aria-hidden style={{ fontSize: 20 }} />
              </button>
            </span>
          </Tooltip>
          {isGroup ? (
            <ExpandMoreRoundedIcon
              className={`${classes.expandIcon} ${expanded ? classes.expandIconOpen : ''}`}
              style={{ fontSize: 20 }}
              aria-hidden
            />
          ) : null}
        </div>
      </div>

      {isGroup ? (
        <Collapse in={expanded} timeout={150}>
          <div className={classes.memberList} role="list" aria-label={`Calls with ${displayName}`}>
            {group.members.map(m => (
              <MemberRow key={m.id} call={m} onOpenCall={onOpenCall} />
            ))}
          </div>
        </Collapse>
      ) : null}
    </div>
  );
};

export default RecentCallCard;
